import { useState } from "react";
import { mediaUrl } from "../../lib/api";
import { track } from "../../lib/tracking";
import { Reveal } from "../Reveal";
import { useLang } from "../../context/LanguageContext";
import { Play } from "lucide-react";

function VideoFrame({ video, poster }) {
  const [playing, setPlaying] = useState(false);

  const onPlay = () => {
    setPlaying(true);
    track("video_view", { title: video.title || "", type: video.type });
  };

  if (video.type === "upload") {
    return (
      <video
        data-testid="course-video-upload"
        src={mediaUrl(video.url)}
        controls
        poster={poster ? mediaUrl(poster) : undefined}
        onPlay={() => track("video_view", { title: video.title || "", type: "upload" })}
        className="w-full h-full object-cover bg-black"
      />
    );
  }

  if (!playing) {
    return (
      <button
        data-testid="course-video-play"
        onClick={onPlay}
        className="group relative w-full h-full bg-[#0B1220] overflow-hidden flex items-center justify-center"
      >
        {poster && (
          <img src={mediaUrl(poster)} alt={video.title} className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
        )}
        <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[#F4F8FF]/90 backdrop-blur transition-transform group-hover:scale-110">
          <Play className="h-7 w-7 text-[#1452F0] ml-1" fill="currentColor" />
        </span>
      </button>
    );
  }

  return (
    <iframe
      data-testid="course-video-iframe"
      src={`${video.url}${video.url.includes("?") ? "&" : "?"}autoplay=1`}
      title={video.title}
      className="w-full h-full"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
}

export function VideoSection({ videos = [], poster }) {
  const { t } = useLang();
  if (!videos.length) return null;
  const [main, ...rest] = videos;

  return (
    <section data-testid="course-videos" className="bg-[#0B1220] py-20 md:py-28">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <Reveal className="max-w-2xl mb-12">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#5B9DFF]">{t("video.overline")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F4F8FF] mt-3">
            {t("video.title")}
          </h2>
        </Reveal>

        <Reveal className="aspect-video w-full overflow-hidden rounded-sm mb-4">
          <VideoFrame video={main} poster={poster} />
        </Reveal>

        {rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {rest.map((v, i) => (
              <Reveal key={i} delay={i * 0.08} className="aspect-video overflow-hidden rounded-sm">
                <VideoFrame video={v} poster={poster} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
